// src/pages/public/Contact.js
import React, { useState } from "react";
import { Helmet } from 'react-helmet-async';
import { motion } from "framer-motion";
import { httpsCallable } from "firebase/functions"; 
import { functions } from "../../firebase"; 
import { COMPANY } from "../../utils/constants";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [status, setStatus] = useState(null); // "loading" | "success" | "error"

  const handleChange = e => setForm(p => ({ ...p, [e.target.name]: e.target.value }));

 const handleSubmit = async e => {
  e.preventDefault();
  setStatus("loading");
  
  try {
    const submitInquiry = httpsCallable(functions, 'submitInquiry');
    await submitInquiry(form);
    
    setStatus("success");
    setForm({ name: "", email: "", phone: "", subject: "", message: "" });
  } catch (err) {
    console.error("Function error:", err);
    setStatus("error");
  }
};

  return (
    <div style={{ paddingTop: "var(--header-h)" }}>
      <Helmet>
        <title>Contact Us - Aimble I.T. Solutions</title>
        <meta name="description" content="Get in touch with Aimble I.T. Solutions in Irosin, Sorsogon. Book a consultation for web development, payment integration, or VA services." />
        <link rel="canonical" href="https://www.aimbletech.com/contact" />
      </Helmet>
      <section className="section-sm" style={{ textAlign: "center" }}>
        <div className="container">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}>
            <div className="section-label" style={{ justifyContent: "center" }}>Contact</div>
            <h1 className="heading-1" style={{ marginBottom: 16 }}>Let's talk about your project</h1>
            <p className="body-lg text-muted" style={{ maxWidth: 480, margin: "0 auto" }}>
              Send us a message or book an appointment. We'll respond within 24 hours.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="section-sm">
        <div className="container">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 48 }}>
            {/* Contact form */}
            <motion.div initial={{ opacity: 0, x: -24 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 }}>
              <h2 className="heading-2" style={{ marginBottom: 8 }}>Send us a message</h2>
              <p style={{ color: "var(--text-muted)", marginBottom: 28, fontSize: "0.875rem" }}>
                Your message goes directly to our team. We'll follow up within one business day.
              </p>

              {status === "success" && (
                <div className="alert alert-success">
                  ✓ Message sent! We'll get back to you within 24 hours.
                </div>
              )}
              {status === "error" && (
                <div className="alert alert-error">
                  ✗ Something went wrong. Try emailing us at {COMPANY.email}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">Full Name *</label>
                    <input name="name" className="form-input" placeholder="Your name" value={form.name} onChange={handleChange} required />
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">Email *</label>
                    <input name="email" type="email" className="form-input" placeholder="your@email.com" value={form.email} onChange={handleChange} required />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Phone (optional)</label>
                  <input name="phone" type="tel" className="form-input" placeholder="+63 XXX XXX XXXX" value={form.phone} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label className="form-label">Subject *</label>
                  <select name="subject" className="form-select" value={form.subject} onChange={handleChange} required>
                    <option value="">Select a topic</option>
                    <option value="Web Development">Web Development</option>
                    <option value="Payment Gateway">Payment Gateway Integration</option>
                    <option value="VA Services">Virtual Assistant Services</option>
                    <option value="Maintenance">Website Maintenance</option>
                    <option value="Custom Quote">Custom Quote Request</option>
                    <option value="Shop Services">Walk-In / Shop Services</option>
                    <option value="General Inquiry">General Inquiry</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Message *</label>
                  <textarea name="message" className="form-textarea" placeholder="Tell us about your project or question..." value={form.message} onChange={handleChange} required style={{ minHeight: 140 }} />
                </div>
                <button type="submit" className="btn btn-primary w-full" style={{ justifyContent: "center" }} disabled={status === "loading"}>
                  {status === "loading" ? "Sending..." : "Send Message →"}
                </button>
              </form>
            </motion.div>

            {/* Right column: info + booking */}
            <motion.div initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }}>
              {/* Contact info cards */}
              <div style={{ marginBottom: 32 }}>
                <h2 className="heading-2" style={{ marginBottom: 20 }}>Other ways to reach us</h2>
                {[
                  { icon: "📘", title: "Facebook", desc: "@aimbleph", link: COMPANY.facebook },
                  { icon: "✉️", title: "Email", desc: COMPANY.email, link: `mailto:${COMPANY.email}` },
                  { icon: "📍", title: "Address", desc: COMPANY.address, link: "https://www.google.com/maps?ll=12.701962,124.042564&z=16&t=m&hl=en&gl=PH&mapclient=embed&cid=10380856658112421272"},
                ].map(c => (
                  <a key={c.title} href={c.link} target={c.link.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer"
                    style={{ display: "flex", gap: 16, alignItems: "flex-start", padding: "16px 20px", background: "var(--surface)", borderRadius: 12, border: "1px solid var(--border)", marginBottom: 12, color: "inherit", transition: "border-color 0.2s" }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = "var(--border-med)"}
                    onMouseLeave={e => e.currentTarget.style.borderColor = "var(--border)"}
                  >
                    <span style={{ fontSize: 22, flexShrink: 0 }}>{c.icon}</span>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: "0.875rem", marginBottom: 2 }}>{c.title}</div>
                      <div style={{ fontSize: "0.8125rem", color: "var(--text-muted)" }}>{c.desc}</div>
                    </div>
                  </a>
                ))}

                {/* Google Map iFrame */}
                 <div style={{ width: "100%", height: 100, borderRadius: 12, overflow: "hidden", background: "var(--navy-mid)", border: "1px solid var(--border-med)", marginTop: "auto" }}>
                   <iframe
                      title="Aimble I.T. Solutions Map"
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3892.172972851579!2d124.0424557665696!3d12.70213076110097!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x33a0cf27306cda77%3A0x90103621630a1d98!2sAimble%20I.T.%20Solutions!5e0!3m2!1sen!2sph!4v1781959547506!5m2!1sen!2sph"
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen=""
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                    ></iframe>
                 </div>
              </div>

              {/* Book appointment */}
              <div style={{ background: "var(--surface)", borderRadius: 16, border: "1px solid var(--border-med)", overflow: "hidden" }}>
                <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--border)" }}>
                  <h3 style={{ fontSize: "0.9375rem", fontWeight: 600 }}>📅 Book an Appointment</h3>
                  <p style={{ fontSize: "0.8125rem", color: "var(--text-muted)", marginTop: 4 }}>Schedule a free consultation via Google Calendar</p>
                </div>
                <div style={{ textAlign: "center", padding: 28 }}>
                  <a href={COMPANY.googleCalendar} target="_blank" rel="noopener noreferrer" className="btn btn-primary">
                    Book a Free Consultation →
                  </a>
                  <p style={{ fontSize: "0.75rem", color: "var(--text-dim)", marginTop: 12 }}>We'll confirm within a few hours</p>
                </div>
              </div>
            </motion.div>
          </div>
          <style>{`@media(max-width:768px){ .container > div { grid-template-columns: 1fr !important; } }`}</style>
        </div>
      </section>
    </div>
  );
}
